import React, { useState, useEffect } from "react";
import { adminGetDashboardStats } from "../api/admin";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaShoppingCart, FaBox, FaUsers, FaCoins, FaNewspaper, FaListUl, FaExclamationTriangle, FaChartLine } from "react-icons/fa";
import "./AdminHome.css";

const AdminHome = () => {
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardStats();
    }, []);

    const fetchDashboardStats = async () => {
        try {
            setLoading(true);
            const response = await adminGetDashboardStats();
            if (response.data) {
                setDashboardData(response.data);
            } else {
                toast.error("Failed to load dashboard statistics");
            }
        } catch (error) {
            console.error("Error fetching dashboard stats:", error);
            toast.error("Error fetching dashboard statistics");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="admin-home-container">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Calculating comprehensive business analytics...</p>
                </div>
            </div>
        );
    }

    if (!dashboardData) {
        return <div className="error-container">Dashboard data unavailable.</div>;
    }

    return (
        <div className="admin-home-container">
            <div className="page-header">
                <h2 className="adminproducts">Dashboard Overview</h2>
                <div className="header-controls-row">
                    <div className="admin-count-badge">System Status: Active</div>
                </div>
            </div>

            <div className="stats-grid">
                <StatCard 
                    title="Total Orders" 
                    value={dashboardData.total_orders} 
                    color="#3699ff" 
                    icon={<FaShoppingCart />} 
                    onClick={() => navigate("/adminDashboard/order")}
                />
                <StatCard 
                    title="Total Users" 
                    value={dashboardData.total_users} 
                    color="#f64e60" 
                    icon={<FaUsers />} 
                    onClick={() => navigate("/adminDashboard/users")}
                />
                <StatCard 
                    title="Products" 
                    value={dashboardData.total_products} 
                    color="#1bc5bd" 
                    icon={<FaBox />} 
                    onClick={() => navigate("/adminDashboard/products")}
                />
                <StatCard 
                    title="Categories" 
                    value={dashboardData.total_categories} 
                    color="#ffa800" 
                    icon={<FaListUl />} 
                    onClick={() => navigate("/adminDashboard/categories")}
                />
                <StatCard 
                    title="Blogs" 
                    value={dashboardData.total_blogs} 
                    color="#8950fc" 
                    icon={<FaNewspaper />} 
                    onClick={() => navigate("/adminDashboard/blogs")}
                />
            </div>

            <div className="financial-grid">
                <FinancialCard 
                    title="Revenue Performance" 
                    stats={dashboardData.revenue_stats} 
                    type="Revenue" 
                    icon={<FaChartLine />} 
                    color="#3699ff"
                />
                <FinancialCard 
                    title="Income Summary" 
                    stats={dashboardData.income_stats} 
                    type="Income" 
                    icon={<FaCoins />} 
                    color="#1bc5bd"
                />
            </div>

            <div className="dashboard-sections">
                {/* Low Stock Alerts Section */}
                <div className="recent-table-card warning-card full-width-card">
                    <div className="card-header-with-icon">
                        <FaExclamationTriangle className="header-icon warning-icon" />
                        <h3>Low Stock Alerts</h3>
                    </div>
                    <div className="table-responsive">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th>Stock Level</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dashboardData.low_stock_products && dashboardData.low_stock_products.length > 0 ? (
                                    dashboardData.low_stock_products.map((item, idx) => (
                                        <tr key={item.id || idx}>
                                            <td className="product-name-cell">{item.name}</td>
                                            <td className="stock-cell">
                                                <span className="stock-number">{item.stock}</span> units
                                            </td>
                                            <td>
                                                <button 
                                                    className="btn-restock"
                                                    onClick={() => {
                                                        const pId = item.productId || item.product_id || item.p_id || item.id;
                                                        const vId = item.variant_id || item.variantId || item.id;
                                                        // Using /variant/ as per recent direct user changes in App.js
                                                        navigate(`/adminDashboard/variant/${pId}`, { 
                                                            state: { 
                                                                highlightVariantId: vId,
                                                                fromDashboard: true
                                                            } 
                                                        });
                                                    }}
                                                >
                                                    Restock
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="3" style={{ textAlign: 'center', padding: '30px', color: '#7e8299' }}>
                                            ✅ All products are well stocked.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ title, value, color, icon, onClick }) => (
    <div className="stat-card" style={{ borderLeft: `5px solid ${color}`, cursor: 'pointer' }} onClick={onClick}>
        <div className="stat-info">
            <span className="stat-title">{title}</span>
            <span className="stat-value">{value.toLocaleString()}</span>
        </div>
        <div className="stat-icon-wrapper" style={{ backgroundColor: `${color}15`, color: color }}>
            {icon}
        </div>
    </div>
);

const FinancialCard = ({ title, stats, type, icon, color }) => (
    <div className="financial-card">
        <div className="f-card-header">
            <div className="f-title-group">
                <span className="f-icon" style={{ color: color }}>{icon}</span>
                <h4>{title}</h4>
            </div>
            <span className="f-total-label">Lifetime {type}</span>
        </div>
        
        <div className="f-main-value">
            ₹{stats[`total_${type.toLowerCase()}`]?.toLocaleString() || 0}
        </div>

        <div className="f-breakdown">
            <div className="f-item">
                <span className="f-label">This Month</span>
                <span className="f-val">₹{stats[`monthly_${type.toLowerCase()}`]?.toLocaleString() || 0}</span>
            </div>
            <div className="f-divider"></div>
            <div className="f-item">
                <span className="f-label">This Year</span>
                <span className="f-val">₹{stats[`yearly_${type.toLowerCase()}`]?.toLocaleString() || 0}</span>
            </div>
        </div>
    </div>
);

export default AdminHome;
